import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@apollo/client";
import { FORGET_PASSWORD } from "@/Graphql/Queries";
import { REACT_APP_PLATFORM_BASE_URL } from "@/Graphql/apolloClient";
import { isValidEmail } from "@/utils/validationUtils";
import { useToast } from "@/hooks/use-toast";


// Define validation schema with custom email validation
const customForgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .refine(isValidEmail, { message: 'Please enter a valid email address' }),
});

type ForgotPasswordFormData = z.infer<typeof customForgotPasswordSchema>;

interface SignInProps {
  base_path?: string;
}


export default function ForgotPassword({
  base_path = "",
}: SignInProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [forgot, { loading }] = useMutation(FORGET_PASSWORD);

  const { toast } = useToast();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(customForgotPasswordSchema),
    defaultValues: {
      email: "",
    },
    mode: 'onChange',
  });

  const email = form.watch('email');

  // Trigger validation on email change
  useEffect(() => {
    if (email) {
      form.trigger('email');
    }
  }, [email, form]);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    const forgotPasswordUrl = import.meta.env.VITE_FORGOT_PASSWORD_URL;
    const FORGOT_PASSWORD_URL = forgotPasswordUrl ? `https://${forgotPasswordUrl}` : '';
    
    try {
      const { data: resp } = await forgot({
        variables: {
          email: data.email,
          platformSource: 'ECOMMERCE',
          platformBaseUrl: REACT_APP_PLATFORM_BASE_URL,
          publisherId: import.meta.env.VITE_APP_COMPANY_ID,
          baseUrl: FORGOT_PASSWORD_URL
        },
      });
      
      if (resp && resp.forgetPassword) {
        const message = resp.forgetPassword.message;
        if (message === 'Provided email address is not registered') {
          setError('The email address is not registered. Please check and try again.');
          toast({
            title: "Error",
            description: 'The email address is not registered. Please check and try again.',
            variant: "destructive",
          });
        } else if (message === 'error.validation') {
          setError('An error occurred. Please try again.');
          toast({
            title: "Error",
            description: 'An error occurred. Please try again.',
            variant: "destructive",
          });
        } else {
          setSuccess(message);
          toast({
            title: "Success",
            description: message,
            variant: "default",
          });
          setIsSubmitted(true);
        }
      }
    } catch (err) {
      setError('Provided email address is not associated with any account');
      toast({
        title: "Error",
        description: 'Provided email address is not associated with any account',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Check your email</h1>
          <p className="text-gray-600 mb-6">
            We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
          </p>
          <Link to={`${base_path}/login`}>
            <Button
              className="w-full btn-primary-gradient hover:bg-primary-700 text-white font-medium py-2.5 transform hover:scale-[1.02] transition-all duration-200">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-emerald-600 mb-2">Forgot Password?</h1>
          <p className="text-gray-600">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 text-sm">{success}</p>
          </div>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="email"
                placeholder="Email Address"
                {...form.register("email")}
                className="px-5 w-full h-12 pl-10 pr-4 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm"
                autoComplete="email"
              />
            </div>
            {form.formState.errors.email && (
              <p className="text-sm text-red-600">{form.formState.errors.email.message}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading || loading || !form.formState.isValid}
            className="w-full h-12 btn-emerald-gradient disabled:opacity-50 text-white font-semibold rounded-lg"
          >
            {isLoading || loading ? "Sending..." : "Send Reset Link"}
          </Button>

          <div className="text-center">
            <Link to={`${base_path}/login`}>
              <span className="text-emerald-600 hover:underline cursor-pointer text-sm">
                <ArrowLeft className="inline mr-1 h-4 w-4" />
                Back To Login
              </span>
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}